import { useEffect, useRef } from 'react'

const defaultIntersectionObserverOptions: IntersectionObserverInit = {
  root: null,
  rootMargin: '0px',
  threshold: 0.01,
}

const useInfiniteScroll = (
  targetRef: React.MutableRefObject<HTMLElement | null>,
  onScroll: () => void,
  _options: Partial<IntersectionObserverInit> = defaultIntersectionObserverOptions,
) => {
  const onScrollRef = useRef(onScroll)
  useEffect(() => {
    onScrollRef.current = onScroll
  })

  const root = _options.root ?? defaultIntersectionObserverOptions.root
  const rootMargin = _options.rootMargin ?? defaultIntersectionObserverOptions.rootMargin
  const threshold = _options.threshold ?? defaultIntersectionObserverOptions.threshold

  const observerRef = useRef<IntersectionObserver | null>(null)
  const observedElRef = useRef<HTMLElement | null>(null)

  // sentinel 이 빈 목록 뒤에 뒤늦게 마운트되는 화면에서는 ref.current 가 채워져도
  // 리렌더가 일어나지 않아 deps 로 감지할 수 없다. 매 커밋마다 element 변화를
  // 직접 비교해, 같은 노드면 재구독을 건너뛰고 바뀐 순간에만 다시 observe 한다.
  useEffect(() => {
    const target = targetRef.current
    if (target === observedElRef.current) return

    observerRef.current?.disconnect()
    observedElRef.current = target

    if (!target) {
      observerRef.current = null
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onScrollRef.current()
      },
      { root, rootMargin, threshold },
    )
    observer.observe(target)
    observerRef.current = observer
  })

  useEffect(
    () => () => {
      observerRef.current?.disconnect()
    },
    [],
  )
}

export default useInfiniteScroll
